import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useModalA11y from '../../hooks/useModalA11y';
import BulkExportDialog from './BulkExportDialog';
import { useStore } from '../../store/store';
import { API_ENDPOINTS } from '../../api/endpoints';
import { DOWNLOAD_LIMIT_EXCEEDED_CODE } from '../../utils/constants/downloadLimits';

/**
 * Modal wrapper around BulkExportDialog.
 *
 * Contract note: `downloads.limit <= 0` or a missing `downloads` prop means
 * "unlimited / uncapped" per the subscription utility convention
 * (see src/utils/constants/subscription.js:26). In that state this component
 * renders nothing — hosts short-circuit before opening the modal.
 *
 * On open, fires POST /bulk-download-precheck with { source, sourceId } and
 * renders the four-field breakdown from the response. While loading, shows a
 * spinner; on network failure, falls back to a "preview unavailable" note
 * with Confirm enabled (precheck is fail-open per BE contract — don't block
 * legitimate downloads on a precheck blip).
 *
 * `includeContacts` marks a contacts download. The BE then prices only the
 * companies that will be in the file, i.e. those with at least one contact
 * (TD-2981), instead of the whole source, and the dialog labels the count
 * accordingly. A `degraded` response means the contact lookup failed and the
 * BE fell back to the whole-source price; that is shown as "preview
 * unavailable" too, because its verdict could refuse an export that fits.
 *
 * `prefillData`, when provided, short-circuits the precheck and renders the
 * dialog directly from the provided envelope. Used for the 403
 * DOWNLOAD_LIMIT_EXCEEDED retake path: a host catches the 403 on an actual
 * export click and re-opens the modal with the rich numbers, so the user
 * sees the precise shortfall without a second round-trip.
 */
const BulkExportConfirmModal = ({
  isOpen,
  totalCompanies,
  downloads,
  source,
  sourceId,
  includeContacts = false,
  prefillData,
  period,
  onConfirm,
  onCancel
}) => {
  const dialogRef = useRef(null);
  const { makeRequest } = useStore();
  const isVisible = isOpen && !!(downloads && downloads.limit > 0);

  const [precheck, setPrecheck] = useState(null);
  const [didFail, setDidFail] = useState(false);

  useModalA11y({ isOpen: isVisible, onClose: onCancel, dialogRef });

  useEffect(() => {
    if (!isVisible) {
      setPrecheck(null);
      setDidFail(false);
      return;
    }
    if (prefillData) return;
    if (!sourceId) {
      setDidFail(true);
      return;
    }
    let stale = false;
    setPrecheck(null);
    setDidFail(false);
    makeRequest({
      endpoint: API_ENDPOINTS.BULK_DOWNLOAD_PRECHECK,
      body: includeContacts ? { source, sourceId, includeContacts: true } : { source, sourceId },
      onSuccess: (data) => {
        if (stale) return;
        if (data?.degraded) setDidFail(true);
        else setPrecheck(data);
      },
      onError: () => {
        if (!stale) setDidFail(true);
      }
    });
    return () => {
      stale = true;
    };
  }, [isVisible, source, sourceId, includeContacts, prefillData, makeRequest]);

  if (!isVisible) return null;

  const data = prefillData || precheck;
  const isLoading = !data && !didFail;
  // Prefer the BE's explicit `allowed` field when present. The 403 retake
  // envelope omits it (per buildLimitExceededBody) but always carries
  // `code: DOWNLOAD_LIMIT_EXCEEDED` — fall back to that signal so we never
  // render a 403 envelope as if it allowed the export (which would trap the
  // user in a Confirm → 403 → reopen → Confirm loop).
  let allowed = true;
  if (data) {
    allowed =
      data.allowed === undefined ? data.code !== DOWNLOAD_LIMIT_EXCEEDED_CODE : data.allowed;
  }

  // Native <dialog> (TD-2120 Item 2): showModal() in useModalA11y gives focus
  // trap, Escape, aria-modal and top-layer rendering — top-layer makes the
  // former @radix-ui/react-portal redundant. The padding-less <dialog> is the
  // backdrop (a click on it light-dismisses via the hook); BulkExportDialog is
  // the visible box.
  return (
    <dialog
      className="bulk-export-modal"
      aria-labelledby="bulk-export-title"
      aria-describedby="bulk-export-breakdown"
      ref={dialogRef}
    >
      <BulkExportDialog
        totalCompanies={data?.total ?? totalCompanies}
        alreadyDownloaded={data?.already_downloaded ?? 0}
        period={period}
        netNew={data?.net_new ?? 0}
        currentCount={data?.current_count ?? null}
        limit={data?.limit ?? downloads.limit ?? 0}
        remainingAfter={data?.remaining_after ?? 0}
        allowed={allowed}
        isLoading={isLoading}
        didFail={didFail}
        isContactsExport={includeContacts}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </dialog>
  );
};

BulkExportConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  totalCompanies: PropTypes.number.isRequired,
  downloads: PropTypes.shape({
    limit: PropTypes.number,
    remaining: PropTypes.number
  }),
  source: PropTypes.oneOf(['project', 'comparables']).isRequired,
  sourceId: PropTypes.string,
  /** The pending export is the contact list (prices only companies in the file). */
  includeContacts: PropTypes.bool,
  prefillData: PropTypes.shape({
    allowed: PropTypes.bool,
    total: PropTypes.number,
    already_downloaded: PropTypes.number,
    net_new: PropTypes.number,
    current_count: PropTypes.number,
    limit: PropTypes.number,
    remaining_after: PropTypes.number
  }),
  // TD-2813: the active contract period, from useSubscriptionStatus. Passed in
  // rather than fetched here so opening the modal still costs one request.
  period: PropTypes.shape({
    isMultiPeriod: PropTypes.bool,
    resetsOn: PropTypes.string
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default BulkExportConfirmModal;
